import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { logoutFromHelper } from "api/Helper";
import {
  GET_ALL_STAFF,
  STAFF_MEMBERS_CONTROLER_URL,
  STAFF_MEMBERS_CONTROLER_URL_ID,
  STAFF_MEMBERS_CONTROLER_URL_ID_DISABLE,
} from "api/Service";
import { SmsModal } from "components/Modal/SmsModal";
import { TabSwitcher } from "components/ui/TabSwitcher/TabSwitcher";
import { pathGenerators } from "utils/routes";

import { logout } from "../../../redux/auth/AuthSlice";
import { removeuser } from "../../../redux/user/userSlice";
import { AddLicenceModal } from "./components/AddLicenceModal";
import { StaffAvailabilitySection } from "./components/StaffAvailabilitySection";
import { UserFormModal } from "./components/UserFormModal";
import { UsersTable } from "./components/UsersTable";
import { useUserStats } from "./hooks/useUserStats";

const user_option = [];
const UserDetailsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const queryClient = useQueryClient();

  const [user, setUser] = useState([]);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectAllChecked] = useState(false);
  const [selectedUsersForSMS, setSelectedUsersForSMS] = useState([]);
  const [isAddLicenceModalOpen, setIsAddLicenceModalOpen] = useState(false);
  // The staff payload that was blocked by the licence limit — adding it back once the
  // licence purchase succeeds retries the user creation that triggered this modal.
  const [pendingUserData, setPendingUserData] = useState(null);
  // Set when "Add User" was clicked while already at the licence limit (no payload yet to
  // retry) — once the licence purchase succeeds, open the Add User form instead.
  const [openFormAfterLicence, setOpenFormAfterLicence] = useState(false);
  // Resolver pair for a createNewUser() call currently blocked on the licence modal — settled
  // by handleLicenceAdded (resolve, with the eventual save result) or the modal's cancel (reject),
  // so UserFormModal's `await onSave(...)` sees the real outcome instead of resolving early.
  const pendingSaveRef = useRef(null);

  const [filter, setFilter] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "user-availability") {
      return tabParam;
    }
    return "all-users";
  });

  const [rosteredStaff, setRosteredStaff] = useState([]);

  let tenantVal = useSelector((state) => state.auth.user?.tenant);
  if (!tenantVal) tenantVal = localStorage.getItem("kinesin-tenant");

  const {
    data: userStats,
    isLoading: isStatsLoading,
    refetch: refetchUserStats,
  } = useUserStats(tenantVal);

  const [, setIsPageLoaded] = useState(false);

  // Memoised: passing a fresh object into the modal re-ran its reset effect on
  // every render of this page, wiping whatever was being typed or picked.
  const selectedStaff = useMemo(
    () => user.find((item) => item.id === selectedTaskId),
    [user, selectedTaskId]
  );

  // The stats endpoint's "deleted" count tracks hard deletes, which the app never does — users
  // are only ever disabled (see toggleUserAccess). Count disabled users from the list we already
  // have, the same source the Access column reads from, rather than that always-zero figure.
  const disabledUsersCount = useMemo(
    () => user.filter((item) => item.disabled).length,
    [user]
  );

  const handleAddTask = () => {
    setSelectedTaskId(null);

    // Known to already be at the limit before the form is even opened: go through the licence
    // flow first rather than letting them fill out the whole form only to hit the 409 on submit.
    if (
      userStats &&
      userStats.licencedUsers != null &&
      userStats.activeUsers >= userStats.licencedUsers
    ) {
      setOpenFormAfterLicence(true);
      setIsAddLicenceModalOpen(true);
      return;
    }

    setIsUserFormOpen(true);
  };

  const handleLicenceAdded = async (quantity = 1) => {
    setIsAddLicenceModalOpen(false);
    toast.success(
      quantity === 1
        ? "Licence added — it will be added to your invoice next month."
        : `${quantity} licences added — they will be added to your invoice next month.`
    );
    refetchUserStats();

    if (pendingUserData) {
      const toRetry = pendingUserData;
      const pending = pendingSaveRef.current;
      pendingSaveRef.current = null;
      setPendingUserData(null);

      // Retry the save that was blocked. Resolve/reject the promise UserFormModal's
      // onSave(...) is still awaiting, so permissions save and the form close only now.
      try {
        const result = await createNewUser(toRetry);
        pending?.resolve(result);
      } catch (error) {
        pending?.reject(error);
      }
      return;
    }

    if (openFormAfterLicence) {
      setOpenFormAfterLicence(false);
      setIsUserFormOpen(true);
    }
  };

  const createNewUser = async (userData) => {
    try {
      const axiosInstance = createAxiosInstance();
      let response;
      if (selectedTaskId) {
        const editedData = { ...userData, id: selectedTaskId };
        response = await axiosInstance.post(
          `${tenantVal}${STAFF_MEMBERS_CONTROLER_URL}`,
          editedData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        response = await axiosInstance.post(
          `${tenantVal}${STAFF_MEMBERS_CONTROLER_URL}`,
          userData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (selectedTaskId) {
        const updatedTasks = user.map((item) =>
          item.id === selectedTaskId ? { ...item, ...response.data } : item
        );
        setUser(updatedTasks);
        toast.success("User updated successfully");
      } else {
        setUser((prevService) => [...prevService, response.data]);
        toast.success("New User created successfully");
      }
      fetchUser();
      refetchUserStats();
      return response.data;
    } catch (error) {
      // All licences are in use: offer the self-serve top-up instead of a dead-end toast, and
      // remember this payload so it can be retried automatically once a licence is added. The
      // caller (UserFormModal's onSave await) stays pending until that retry settles, rather
      // than seeing this as a completed (failed) save.
      if (error?.response?.status === 409) {
        setPendingUserData(userData);
        setIsAddLicenceModalOpen(true);
        return new Promise((resolve, reject) => {
          pendingSaveRef.current = { resolve, reject };
        });
      }
      errorHandling(error);
      return null;
    }
  };

  /**
   * Enable or disable a staff member. The disable endpoint wants the whole
   * record, so the current one is fetched and posted back with `disabled`
   * flipped — one operation rather than staging the user in state first.
   *
   * The body is the fetched record spread whole, not a hand-listed subset. The subset it used to send
   * left out gmsNumber, deaspPanelNumber and qualification, and the backend then wrote NULL over every
   * omitted column — so disabling and re-enabling a doctor silently erased their GMS contract number
   * (KIN-1935). Since KIN-1724 an omitted field is kept, but a hand-listed subset would still send ""
   * for anything it blanked, so the record still goes back whole.
   */
  const toggleUserAccess = async (staffId) => {
    try {
      const axiosInstance = createAxiosInstance();
      const { data: current } = await axiosInstance.get(
        `${tenantVal}${STAFF_MEMBERS_CONTROLER_URL_ID}${staffId}`
      );

      await axiosInstance.post(
        `${tenantVal}${STAFF_MEMBERS_CONTROLER_URL_ID_DISABLE}`,
        {
          ...current,
          disabled: !current.disabled,
          tenant: tenantVal,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      toast.success(
        current.disabled ? "User enabled" : "User disabled"
      );
      fetchUser();
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
    } catch (error) {
      errorHandling(error);
      toast.error("Failed to update user access");
    }
  };

  const handleEditTask = (id) => {
    setSelectedTaskId(id);
    setIsUserFormOpen(true);
  };

  const handleSMSModalClose = (userId) => {
    const updatedUser = user.map((item) =>
      item.id === userId ? { ...item, checked: false } : item
    );
    setUser(updatedUser);

    const selectedForSMS = updatedUser
      .filter((item) => item.checked)
      .map((item) => item.id);
    setSelectedUsersForSMS(selectedForSMS);
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantVal]);

  useEffect(() => {
    const filtered = user.filter((u) =>
      (u.firstName + " " + (u.lastName || ""))
        .toLowerCase()
        .includes(filter.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [filter, user]);

  useEffect(() => {
    if (tenantVal) {
      fetchRosteredStaff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantVal]);

  const fetchRosteredStaff = async () => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`${tenantVal}${GET_ALL_STAFF}`);
      if (response && response.data) {
        setRosteredStaff(response.data);
      }
    } catch (error) {
      console.error("Error fetching rostered staff:", error);
      setRosteredStaff([]);
    }
  };

  const fetchUser = async () => {
    try {
      setIsLoading(true);
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}${STAFF_MEMBERS_CONTROLER_URL}`
      );
      if (response && response.data) {
        setUser(response.data);
        setIsPageLoaded(true);
      }
    } catch (error) {
      errorHandling(error);
    } finally {
      setIsLoading(false);
    }
  };

  const errorHandling = (error) => {
    console.error(error);
    toast.error(error.message);
    if (error && error.response && error.response.status) {
      if (error.response.status === 403) {
        logoutFromHelper();
        dispatch(logout());
        dispatch(removeuser());
        navigate(pathGenerators.auth.logout());
      }
    }
  };

  const handleCopyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Email copied to clipboard");
    } catch (err) {
      toast.error("Failed to copy email");
    }
  };

  // A non-recurring availability is a single date: the start date. The end
  // date and the weekday flags do not apply, so they are neither editable nor
  // sent — the backend derives them from the start date (KIN-1671).

  const tabSwitcher = (
    <TabSwitcher
      options={[
        { value: "all-users", label: "All users" },
        { value: "user-availability", label: "User availability" },
      ]}
      value={activeTab}
      onChange={setActiveTab}
      fullWidth
    />
  );

  return (
    <>
      <div className="user_detailsbox">
        {activeTab === "all-users" && (
          <UsersTable
            tabs={tabSwitcher}
            users={filteredUsers}
            isLoading={isLoading}
            search={filter}
            onSearchChange={setFilter}
            onAddUser={handleAddTask}
            onEditUser={handleEditTask}
            onToggleAccess={toggleUserAccess}
            onCopyEmail={handleCopyEmail}
            stats={userStats}
            isStatsLoading={isStatsLoading}
            disabledUsersCount={disabledUsersCount}
          />
        )}

        {activeTab === "user-availability" && (
          <StaffAvailabilitySection
            tabs={tabSwitcher}
            tenantVal={tenantVal}
            staffMembers={rosteredStaff}
          />
        )}
      </div>
      <SmsModal
        tenantVal={tenantVal}
        selectedUsers={selectedUsersForSMS}
        user_option={user_option}
        handleSMSModalClose={handleSMSModalClose}
        selectAllChecked={selectAllChecked}
      />
      <UserFormModal
        isOpen={isUserFormOpen}
        onClose={() => setIsUserFormOpen(false)}
        staffId={selectedTaskId}
        staff={selectedStaff}
        onSave={createNewUser}
      />
      <AddLicenceModal
        isOpen={isAddLicenceModalOpen}
        onClose={() => {
          setIsAddLicenceModalOpen(false);
          setPendingUserData(null);
          setOpenFormAfterLicence(false);
          if (pendingSaveRef.current) {
            pendingSaveRef.current.reject(
              new Error("Licence addition was cancelled")
            );
            pendingSaveRef.current = null;
          }
        }}
        onSuccess={handleLicenceAdded}
        tenantVal={tenantVal}
      />
    </>
  );
};

export { UserDetailsPage };
